import { z } from "zod";

import { WorkspaceSummarySchema } from "./core";

// List workspaces — wrapped in an object so additive top-level fields
// (pagination cursor, total count, etc.) can land later without breaking
// every consumer of `useList`. Mirrors the codebase pattern (see ZCredentials).
export const ListWorkspacesResponseSchema = z.object({
  workspaces: z.array(WorkspaceSummarySchema)
});

/** Opt-in expansions for the list endpoint. */
export const WORKSPACE_LIST_INCLUDE = ["folder-stats"] as const;

/**
 * Query params for `GET /workspaces`. `include` is a comma-separated list of
 * opt-in expansions (currently just `folder-stats`); unknown tokens are
 * rejected so typos surface as a 400 instead of being silently ignored.
 */
export const ListWorkspacesParamsSchema = z.object({
  include: z
    .string()
    .optional()
    .transform((value) =>
      value
        ? value
            .split(",")
            .map((token) => token.trim())
            .filter(Boolean)
        : []
    )
    .pipe(z.array(z.enum(WORKSPACE_LIST_INCLUDE)))
});

// Create workspace.
//
// `.strict()`: name is the ONLY field a tenant may set. `isShared` is not a
// user-facing knob — a workspace is made shared (cross-org) exclusively by a
// Nexus platform admin via `POST /admin/shared-workspaces` (NEX-2292). Were
// this schema permissive, a body like `{ name, isShared: true }` would be
// silently stripped, leaving callers to believe the toggle landed (NEX-2357).
// Rejecting unknown keys surfaces the attempt as a 400 instead of a no-op,
// matching how `ListWorkspacesParamsSchema` rejects unknown `include` tokens.
export const CreateWorkspaceBodySchema = z
  .object({
    name: z.string().trim().min(1, "name is required").max(100, "name must be ≤ 100 characters")
  })
  .strict();

// Rename workspace (PATCH /:slug) — slug path-var + new name
export const WorkspaceSlugPathVarsSchema = z.object({
  slug: z.string().min(1)
});

// Rename workspace body. `.strict()` for the same reason as create: PATCH only
// renames. There is no path for a tenant to flip `isShared` after creation —
// passing it (or any other unknown key) is a 400, never a silent no-op.
export const RenameWorkspaceBodySchema = z
  .object({
    name: z.string().trim().min(1).max(100)
  })
  .strict();
